import { useCallback, useEffect, useState } from 'react';
import useTranslation from 'v2/layouts/OrganizationWorkspaceLayout/components/OrganizationDetailsSection/components/PesticideLicensesCard/hooks/useTranslation';
import type { PesticideLicenseResponse } from 'v2/layouts/OrganizationWorkspaceLayout/components/OrganizationDetailsSection/components/PesticideLicensesCard/types';

import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';
import usePesticideLicenses from 'hooks/usePesticideLicenses';
import useRoles from 'hooks/useRoles';
import useToast from 'hooks/useToast';

const usePesticideLicensesTable = () => {
    const { deletePesticideLicense, fetchPesticideLicenses } = usePesticideLicenses();
    const { canManageOrganizations } = useRoles();
    const { navigate } = useNavigation();
    const { editPesticideLicensePage, organizationNewPesticideLicense } = usePageRoutes();
    const { broadcastToast } = useToast();
    const { DELETE_ERROR, DELETE_SUCCESS, LOAD_ERROR } = useTranslation();

    const [licenses, setLicenses] = useState<PesticideLicenseResponse[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [pendingDeleteLicenseId, setPendingDeleteLicenseId] = useState<string | null>(null);

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const response = await fetchPesticideLicenses(pageToken);

            setLicenses(currentLicenses =>
                pageToken ? [...currentLicenses, ...response.objects] : response.objects
            );
            setHasNextPage(response.hasNext);
            setNextPageToken(response.nextPageToken ?? null);
        },
        [fetchPesticideLicenses]
    );

    useEffect(() => {
        const loadFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            broadcastToast({ text: LOAD_ERROR, type: 'error' });
        } finally {
            setIsFetchingMore(false);
        }
    }, [LOAD_ERROR, broadcastToast, hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleAdd = useCallback(
        () => navigate(organizationNewPesticideLicense),
        [navigate, organizationNewPesticideLicense]
    );

    const handleEdit = useCallback(
        (licenseId: string) => navigate(editPesticideLicensePage(licenseId)),
        [editPesticideLicensePage, navigate]
    );

    const handleDeleteRequest = useCallback(
        (licenseId: string) => setPendingDeleteLicenseId(licenseId),
        []
    );

    const handleDeleteCancel = useCallback(() => setPendingDeleteLicenseId(null), []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!pendingDeleteLicenseId) {
            return;
        }

        try {
            await deletePesticideLicense(pendingDeleteLicenseId);
        } catch {
            broadcastToast({ text: DELETE_ERROR, type: 'error' });
            setPendingDeleteLicenseId(null);

            return;
        }

        setLicenses(currentLicenses =>
            currentLicenses.filter(licenseItem => licenseItem.id !== pendingDeleteLicenseId)
        );
        setPendingDeleteLicenseId(null);

        broadcastToast({ text: DELETE_SUCCESS, type: 'success' });
    }, [
        DELETE_ERROR,
        DELETE_SUCCESS,
        broadcastToast,
        deletePesticideLicense,
        pendingDeleteLicenseId
    ]);

    return {
        canManage: Boolean(canManageOrganizations),
        handleAdd,
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleEdit,
        handleLoadMore,
        hasNextPage,
        isDeleteConfirmOpen: pendingDeleteLicenseId !== null,
        isFetchingMore,
        isLoading,
        licenses
    };
};

export default usePesticideLicensesTable;
